import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Plane, Calendar, MapPin, Users, ArrowRight, Clock } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cities = [
  'Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Kolkata', 'Hyderabad',
  'Pune', 'Ahmedabad', 'Jaipur', 'Goa', 'Kochi', 'Chandigarh'
];

export default function BookFlight() {
  const [searchData, setSearchData] = useState({
    from_city: '',
    to_city: '',
    date: ''
  });
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [passengerData, setPassengerData] = useState({
    passenger_name: '',
    passenger_email: '',
    passenger_phone: ''
  });
  const [bookingStep, setBookingStep] = useState('search'); // search, select, confirm
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/flights/search`, searchData);
      setFlights(response.data);
      setBookingStep('select');
    } catch (error) {
      toast.error('Failed to search flights');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFlight = (flight) => {
    setSelectedFlight(flight);
    setPassengerData({
      passenger_name: user?.name || '',
      passenger_email: user?.email || '',
      passenger_phone: user?.phone || ''
    });
    setBookingStep('confirm');
  };

  const handleConfirmBooking = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/bookings`, {
        flight_id: selectedFlight.id,
        ...passengerData
      });
      toast.success('Booking confirmed successfully!');
      navigate('/my-bookings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="book-flight-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
            Book Your Flight
          </h1>
          <p className="text-slate-400 text-lg">Search and book domestic flights at flat rates</p>
        </motion.div>

        {bookingStep === 'search' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-slate-900/80 border-slate-800 p-6 md:p-8 max-w-3xl mx-auto" data-testid="flight-search-card">
              <form onSubmit={handleSearch} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-slate-300 mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-amber-400" /> From
                    </Label>
                    <select
                      data-testid="from-city-select"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      value={searchData.from_city}
                      onChange={(e) => setSearchData({ ...searchData, from_city: e.target.value })}
                      required
                    >
                      <option value="">Select departure city</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300 mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-amber-400" /> To
                    </Label>
                    <select
                      data-testid="to-city-select"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      value={searchData.to_city}
                      onChange={(e) => setSearchData({ ...searchData, to_city: e.target.value })}
                      required
                    >
                      <option value="">Select arrival city</option>
                      {cities.filter((c) => c !== searchData.from_city).map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-amber-400" /> Travel Date
                  </Label>
                  <Input
                    data-testid="travel-date-input"
                    type="date"
                    className="bg-slate-800 border-slate-700 text-white"
                    value={searchData.date}
                    onChange={(e) => setSearchData({ ...searchData, date: e.target.value })}
                    required
                  />
                </div>
                <Button
                  data-testid="search-flights-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-3 rounded-lg"
                >
                  {loading ? 'Searching...' : (
                    <span className="flex items-center justify-center gap-2">
                      <Search className="w-5 h-5" /> Search Flights
                    </span>
                  )}
                </Button>
              </form>
            </Card>
          </motion.div>
        )}

        {bookingStep === 'select' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
            data-testid="flight-results"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {searchData.from_city} <ArrowRight className="inline w-5 h-5 text-amber-400" /> {searchData.to_city}
              </h2>
              <Button
                data-testid="back-to-search-btn"
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={() => setBookingStep('search')}
              >
                New Search
              </Button>
            </div>
            {flights.map((flight) => (
              <Card
                key={flight.id}
                data-testid={`flight-card-${flight.id}`}
                className="bg-slate-900/80 border-slate-800 p-5 hover:border-amber-500/50 transition-colors cursor-pointer"
                onClick={() => handleSelectFlight(flight)}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <Plane className="w-8 h-8 text-amber-400" />
                    <div>
                      <p className="font-semibold text-white">{flight.airline}</p>
                      <p className="text-sm text-slate-400">{flight.flight_number}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-center">
                    <div>
                      <p className="text-lg font-bold text-white">{flight.departure_time}</p>
                      <p className="text-xs text-slate-400">{flight.departure_city}</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <Clock className="w-4 h-4 text-slate-500" />
                      <p className="text-xs text-slate-500">{flight.duration}</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-white">{flight.arrival_time}</p>
                      <p className="text-xs text-slate-400">{flight.arrival_city}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-amber-400">₹{flight.price.toLocaleString()}</p>
                    <p className="text-xs text-slate-400">{flight.available_seats} seats left</p>
                  </div>
                </div>
              </Card>
            ))}
          </motion.div>
        )}

        {bookingStep === 'confirm' && selectedFlight && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-slate-900/80 border-slate-800 p-6 md:p-8 max-w-3xl mx-auto" data-testid="booking-confirm-card">
              <h2 className="text-2xl font-bold text-white mb-6">Confirm Booking</h2>
              <div className="bg-slate-800/60 rounded-lg p-4 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Plane className="w-6 h-6 text-amber-400" />
                  <div>
                    <p className="font-semibold text-white">{selectedFlight.airline} — {selectedFlight.flight_number}</p>
                    <p className="text-sm text-slate-400">
                      {selectedFlight.departure_city} → {selectedFlight.arrival_city} &middot; {selectedFlight.departure_time} - {selectedFlight.arrival_time}
                    </p>
                  </div>
                </div>
                <p className="text-xl font-bold text-amber-400">₹{selectedFlight.price.toLocaleString()}</p>
              </div>
              <form onSubmit={handleConfirmBooking} className="space-y-5">
                <div>
                  <Label className="text-slate-300 mb-1 flex items-center gap-2">
                    <Users className="w-4 h-4 text-amber-400" /> Passenger Name
                  </Label>
                  <Input
                    data-testid="passenger-name-input"
                    className="bg-slate-800 border-slate-700 text-white"
                    value={passengerData.passenger_name}
                    onChange={(e) => setPassengerData({ ...passengerData, passenger_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300 mb-1">Email</Label>
                  <Input
                    data-testid="passenger-email-input"
                    type="email"
                    className="bg-slate-800 border-slate-700 text-white"
                    value={passengerData.passenger_email}
                    onChange={(e) => setPassengerData({ ...passengerData, passenger_email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300 mb-1">Phone</Label>
                  <Input
                    data-testid="passenger-phone-input"
                    type="tel"
                    className="bg-slate-800 border-slate-700 text-white"
                    value={passengerData.passenger_phone}
                    onChange={(e) => setPassengerData({ ...passengerData, passenger_phone: e.target.value })}
                    required
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button
                    data-testid="back-to-results-btn"
                    type="button"
                    variant="outline"
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    onClick={() => setBookingStep('select')}
                  >
                    Back
                  </Button>
                  <Button
                    data-testid="confirm-booking-btn"
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-3 rounded-lg"
                  >
                    {loading ? 'Confirming...' : 'Confirm Booking'}
                  </Button>
                </div>
              </form>
            </Card>
          </motion.div>
        )}
      </div>
      <Footer />
    </div>
  );
}